import { useEffect, useState } from 'react';
import axios from 'axios';
import { Receipt, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RECENT_LIMIT = 5;

const fmtINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const TYPE_COLORS = {
  ORDER_PAYMENT: 'bg-blue-100 text-blue-700 border-blue-200',
  RESTAURANT_SETTLEMENT: 'bg-purple-100 text-purple-700 border-purple-200',
  RESTAURANT_EARNING: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  DELIVERY_EARNING: 'bg-amber-100 text-amber-700 border-amber-200',
  DAILY_TARGET_BONUS: 'bg-lime-100 text-lime-800 border-lime-200',
  COD_COLLECTION: 'bg-orange-100 text-orange-700 border-orange-200',
  WALLET_CREDIT: 'bg-teal-100 text-teal-700 border-teal-200',
  WALLET_DEBIT: 'bg-rose-100 text-rose-700 border-rose-200'
};

const STATUS_COLORS = {
  SUCCESS: 'bg-green-100 text-green-700 border-green-200',
  PENDING: 'bg-gray-100 text-gray-700 border-gray-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200',
  REFUNDED: 'bg-purple-100 text-purple-700 border-purple-200'
};

// "who" this transaction is with — whichever of user/restaurant/agent the
// backend's transaction lookup actually resolved (see routes/transactions.js).
const whoLabel = (t) =>
  t.user?.name || t.user?.phone ||
  t.restaurant?.name ||
  t.agent?.name ||
  null;

export default function RecentTransactionsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/transactions`, {
        params: { limit: RECENT_LIMIT, skip: 0 }
      });
      setRows(r.data?.transactions || []);
    } catch (_) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
      data-testid="dashboard-recent-transactions"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-sky-100 text-sky-700">
            <Receipt size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Recent Transactions
            </p>
            <p className="text-xs text-gray-500">
              Latest {RECENT_LIMIT} across the platform
            </p>
          </div>
        </div>

        <Link
          to="/transactions"
          data-testid="recent-transactions-view-all"
          className="text-xs font-medium text-orange-700 hover:underline flex items-center gap-0.5 shrink-0"
        >
          View all
          <ChevronRight size={14} />
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-gray-400" size={20} />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-8">
          No transactions yet.
        </p>
      ) : (
        <div className="divide-y divide-gray-100" data-testid="recent-transactions-list">
          {rows.map((t) => (
            <div
              key={t.id}
              data-testid={`recent-transaction-row-${t.id}`}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${TYPE_COLORS[t.type] ||
                      'bg-gray-100 text-gray-700 border-gray-200'}`}
                  >
                    {t.type || '—'}
                  </span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_COLORS[t.status] ||
                      'bg-gray-100 text-gray-700 border-gray-200'}`}
                  >
                    {t.status || '—'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {whoLabel(t) || 'Unknown'}
                  {t.orderNumber ? ` · Order #${t.orderNumber}` : ''}
                  {' · '}
                  {timeAgo(t.createdAt)}
                </p>
              </div>
              <span className="text-sm font-semibold text-gray-900 shrink-0">
                {fmtINR(t.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
